import React, { useEffect, useState } from 'react';
import axios from 'axios';
import AttachmentCardView from './AttachmentCardView.jsx';
import AttachmentListView from './AttachmentListView.jsx';
import AttachmentEditor from './AttachmentEditor.jsx';
import AttachmentPreviewModal from './AttachmentPreviewModal.jsx';
import API_ENDPOINTS from '../ApiEndPoints';
import { classNames } from './helpers';

const VIEW_STORAGE_KEY = 'global_document_explorer_view';

const useDebouncedValue = (value, delay = 300) => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
};

const getInitialView = () => {
  if (typeof window === 'undefined') {
    return 'list';
  }
  return localStorage.getItem(VIEW_STORAGE_KEY) || 'list';
};

const DocumentExplorer = () => {
  const [viewMode, setViewMode] = useState(getInitialView);
  const [search, setSearch] = useState('');
  const [instanceId, setInstanceId] = useState('');
  const [ownerId, setOwnerId] = useState('');
  const [tag, setTag] = useState('');
  const [visibility, setVisibility] = useState('all');
  const [sort, setSort] = useState('created_at');
  const [direction, setDirection] = useState('desc');
  const [perPage, setPerPage] = useState(25);
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, per_page: 25, total: 0, last_page: 1 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [previewAttachment, setPreviewAttachment] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  const debouncedSearch = useDebouncedValue(search, 300);
  const debouncedInstance = useDebouncedValue(instanceId, 300);
  const debouncedOwner = useDebouncedValue(ownerId, 300);
  const debouncedTag = useDebouncedValue(tag, 300);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem(VIEW_STORAGE_KEY, viewMode);
    }
  }, [viewMode]);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        page,
        per_page: perPage,
        search: debouncedSearch || undefined,
        visibility,
        sort,
        direction,
        tag: debouncedTag || undefined,
        instance_id: debouncedInstance || undefined,
        owner_id: debouncedOwner || undefined,
      };
      const { data } = await axios.get(API_ENDPOINTS.attachments.documents, { params });
      setRows(data.data || []);
      setPagination((prev) => (data.pagination ? data.pagination : prev));
    } catch (e) {
      setError(e?.response?.data?.message || 'Unable to load documents');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, debouncedInstance, debouncedOwner, debouncedTag, visibility, sort, direction, page, perPage]);

  const openEditor = (attachment) => {
    setSelected(attachment);
    setEditorOpen(true);
  };

  const closeEditor = () => {
    setSelected(null);
    setEditorOpen(false);
  };

  const handleSave = async (payload) => {
    if (!selected?.instance?.id) {
      setError('Missing instance context for attachment');
      return;
    }
    setSaving(true);
    try {
      const url = API_ENDPOINTS.attachments.update(selected.instance.id, selected.id);
      await axios.put(url, payload);
      closeEditor();
      load();
    } catch (e) {
      setError(e?.response?.data?.message || 'Unable to save attachment');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (attachment) => {
    if (!attachment?.instance?.id) {
      setError('Missing instance context for attachment');
      return;
    }
    try {
      const url = API_ENDPOINTS.attachments.delete(attachment.instance.id, attachment.id);
      await axios.delete(url);
      load();
    } catch (e) {
      setError(e?.response?.data?.message || 'Unable to delete attachment');
    }
  };

  const openPreview = (attachment) => {
    if (!attachment) return;
    setPreviewAttachment(attachment);
    setPreviewOpen(true);
  };

  const closePreview = () => {
    setPreviewAttachment(null);
    setPreviewOpen(false);
  };

  const totalPages = Math.max(1, pagination?.last_page || 1);

  return (
    <div className="panel panel-default">
      <div className="flex flex-col gap-2 panel-heading sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h4 className="m-0">Document Explorer</h4>
          <div className="text-xs text-gray-500">Search every attachment across the tenant</div>
        </div>
        <div className="btn-group btn-group-xs">
          <button type="button" className={classNames('btn btn-default', viewMode === 'cards' && 'active')} onClick={() => setViewMode('cards')}>Card view</button>
          <button type="button" className={classNames('btn btn-default', viewMode === 'list' && 'active')} onClick={() => setViewMode('list')}>List view</button>
        </div>
      </div>
      <div className="space-y-4 panel-body">
        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
          <div>
            <label className="text-xs text-gray-600">Search</label>
            <input type="text" className="form-control" value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} placeholder="Title, description, tags" />
          </div>
          <div>
            <label className="text-xs text-gray-600">Instance ID</label>
            <input type="text" className="form-control" value={instanceId} onChange={(e) => { setInstanceId(e.target.value); setPage(1); }} placeholder="Optional" />
          </div>
          <div>
            <label className="text-xs text-gray-600">Owner ID</label>
            <input type="text" className="form-control" value={ownerId} onChange={(e) => { setOwnerId(e.target.value); setPage(1); }} placeholder="Optional" />
          </div>
          <div>
            <label className="text-xs text-gray-600">Tag</label>
            <input type="text" className="form-control" value={tag} onChange={(e) => { setTag(e.target.value); setPage(1); }} placeholder="Exact tag" />
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <label className="text-xs text-gray-600">Visibility</label>
            <select className="form-control input-sm" value={visibility} onChange={(e) => { setVisibility(e.target.value); setPage(1); }}>
              <option value="all">All</option>
              <option value="public">Public</option>
              <option value="private">Private</option>
            </select>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs text-gray-600">Sort</label>
            <select className="form-control input-sm" value={`${sort}:${direction}`} onChange={(e) => {
              const [nextSort, nextDirection] = e.target.value.split(':');
              setSort(nextSort);
              setDirection(nextDirection);
              setPage(1);
            }}>
              <option value="created_at:desc">Newest</option>
              <option value="created_at:asc">Oldest</option>
              <option value="title:asc">Title A-Z</option>
              <option value="title:desc">Title Z-A</option>
              <option value="owner_id:asc">Owner (asc)</option>
              <option value="owner_id:desc">Owner (desc)</option>
            </select>
          </div>
          <div>
            <select className="form-control input-sm" value={perPage} onChange={(e) => {
              setPerPage(Number(e.target.value));
              setPage(1);
            }}>
              {[10, 25, 50].map((size) => (
                <option key={size} value={size}>{size} / page</option>
              ))}
            </select>
          </div>
        </div>

        {error && <div className="alert alert-danger">{error}</div>}

        {loading ? (
          <div className="py-5 text-center text-muted">Loading documents…</div>
        ) : (
          <>
            {viewMode === 'cards'
              ? (
                <AttachmentCardView
                  items={rows}
                  onEdit={openEditor}
                  onDelete={handleDelete}
                  onPreview={openPreview}
                  showInstance
                />
                )
              : (
                <AttachmentListView
                  items={rows}
                  onEdit={openEditor}
                  onDelete={handleDelete}
                  onPreview={openPreview}
                  showInstance
                />
                )}
            <div className="flex flex-col gap-3 pt-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="text-xs text-gray-500">
                Page {pagination.page || 1} of {totalPages} • {pagination.total || rows.length} attachment(s)
              </div>
              <div className="btn-group btn-group-xs">
                <button type="button" className="btn btn-default" disabled={page <= 1} onClick={() => setPage((prev) => Math.max(1, prev - 1))}>Previous</button>
                <button type="button" className="btn btn-default" disabled={page >= totalPages} onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}>Next</button>
              </div>
            </div>
          </>
        )}
      </div>

      <AttachmentEditor
        attachment={selected}
        open={editorOpen}
        onClose={closeEditor}
        onSave={handleSave}
        busy={saving}
      />
      <AttachmentPreviewModal attachment={previewAttachment} open={previewOpen} onClose={closePreview} />
    </div>
  );
};

export default DocumentExplorer;
